import { parseInstant } from './dateTime';
import { supabase } from './supabaseClient';

const normalizeBookingStatus = (value) => String(value || '').trim().toLowerCase().replace(/\s+/g, '_');

const stageMessageMap = {
  initial_consultation: {
    title: 'Initial Consultation',
    body: 'Your project has moved to the initial consultation stage.',
  },
  proposal_quotation: {
    title: 'Proposal & Contract',
    body: 'Please review the proposal and contract for your event.',
  },
  proposal_contract: {
    title: 'Proposal & Contract',
    body: 'Please review the proposal and contract for your event.',
  },
  agreement_confirmation: {
    title: 'Proposal & Contract',
    body: 'Please review and confirm the agreement for your event.',
  },
  contract_signing: {
    title: 'Proposal & Contract',
    body: 'Please review and sign the contract for your event.',
  },
  event_preparation: {
    title: 'Event Preparation',
    body: 'Your event is now in the preparation phase.',
  },
  event_live: {
    title: 'Event is Live! 🚀',
    body: 'Your event is now live! View real-time analytics on your dashboard.',
  },
  booking_details_updated: {
    title: 'Booking Details Updated',
    body: 'Your booking details have been updated by the admin.',
  },
  declined: {
    title: 'Booking Declined',
    body: 'Your booking was declined.',
  },
};

const getDeclinedNotificationMessage = (booking) => {
  const reason = String(booking?.decline_reason || '').trim();
  return {
    title: 'Booking Declined',
    body: reason
      ? `Your booking was declined. Reason: ${reason}`
      : 'Your booking was declined.',
  };
};

export const getBookingNotificationMessage = (booking) => {
  const stage = normalizeBookingStatus(booking?.project_stage);
  const status = normalizeBookingStatus(booking?.status || booking?.booking_status);

  if (stage === 'declined' || status === 'declined') {
    return getDeclinedNotificationMessage(booking);
  }

  if (stageMessageMap[stage]) {
    return stageMessageMap[stage];
  }

  if (status === 'in_review' || status === 'pending_review') {
    return notificationMessages.admin_review;
  }

  if (notificationMessages[status]) {
    return notificationMessages[status];
  }

  return null;
};

export const buildBookingNotification = (booking, { read = false, idPrefix = 'booking' } = {}) => {
  const message = getBookingNotificationMessage(booking);
  if (!message) return null;

  const createdAt = parseInstant(booking?.updated_at || booking?.created_at) || new Date();
  const status = normalizeBookingStatus(booking?.status || booking?.booking_status);
  const stage = normalizeBookingStatus(booking?.project_stage);
  const suffix = stage || status || 'update';

  return {
    id: `${idPrefix}-${booking?.id || 'unknown'}-${suffix}`,
    title: message.title,
    body: message.body,
    bookingId: booking?.id,
    eventName: booking?.event_name,
    createdAt,
    read,
  };
};

export const subscribeToBookingUpdates = (userId, onUpdate) => {
  const subscription = supabase
    .channel(`bookings:${userId}`)
    .on(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'public',
        table: 'bookings',
        filter: `requester_email=eq.${userId}`,
      },
      (payload) => {
        if (onUpdate) {
          onUpdate(payload.new);
        }
      }
    )
    .subscribe();

  return subscription;
};

export const showNotification = () => {
  // OS banners are delivered by the service worker via Web Push.
};

export const requestNotificationPermission = async () => {
  if ('Notification' in window) {
    if (Notification.permission === 'granted') {
      return true;
    }

    if (Notification.permission !== 'denied') {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    }
  }
  return false;
};

export const notificationMessages = {
  admin_review: {
    title: 'Admin Review Started',
    body: 'Your booking request is now under admin review.',
  },
  approved: {
    title: 'Booking Approved! 🎉',
    body: 'Your booking has been accepted and added to your projects.',
  },
  initial_consultation: {
    title: 'Initial Consultation',
    body: 'Your project has moved to the initial consultation stage.',
  },
  proposal_quotation: {
    title: 'Proposal & Contract',
    body: 'Please review the proposal and contract for your event.',
  },
  proposal_contract: {
    title: 'Proposal & Contract',
    body: 'Please review the proposal and contract for your event.',
  },
  event_preparation: {
    title: 'Event Preparation',
    body: 'Your event is now in the preparation phase.',
  },
  event_live: {
    title: 'Event is Live! 🚀',
    body: 'Your event is now live! View real-time analytics on your dashboard.',
  },
  declined: {
    title: 'Booking Declined',
    body: 'Your booking was declined.',
  },
};
